import axios, { type AxiosRequestConfig, type Method } from "axios";
import { type AuthenticationService, type UserInfo } from "./authentication-service";
import { type AuthenticationServiceConfiguration } from "./authentication-service-configuration";
import { HtmlStorageHelper } from "./html-storage-helper";


const oauthOperationPaths: Record<OAuthAgentOperationType, string> = {
    login: "/oauth2/sign_in",
    logout: "/oauth2/sign_out",
    start: "/oauth2/start",
    userInfo: "/oauth2/userinfo",

};

type OAuthAgentOperationType = "login" | "logout" | "userInfo" | "start";

class AuthenticationServiceImpl implements AuthenticationService {
    private readonly _configuration: AuthenticationServiceConfiguration;
    private _userinfo: UserInfo | null = null;
    private _lastUserInfo: UserInfo | null = null;
    private _initialized: boolean = false;

    constructor(configuration: AuthenticationServiceConfiguration) {
        this._configuration = configuration;
        this.onUserInfoChanged((o, n) => this.userInfoChanged(o, n));
    }
    get isInititalized(): boolean {
        return this._initialized;
    }


    isLoggedIn(): boolean {
        return this._userinfo != null;
    }

    public async init() {
        try {
            await this.refreshUserInfo();
            return true;
        }
        catch (e) {
            this.clearLoginState();
            return false;
        }
        finally {
            this._initialized = true;
        }

    }

    public login(redirect: string) {
        const url = `${this._configuration.oauthProxyBaseUrl}${oauthOperationPaths["login"]}?rd=${redirect}`;
        location.href = url;
    }

    public start(redirect: string) {
        const url = `${this._configuration.oauthProxyBaseUrl}${oauthOperationPaths["start"]}?rd=${redirect}`;
        location.href = url;
    }

    public async refreshUserInfo(): Promise<UserInfo> {
        const response = await this._configuration.userInfoGetter();
        this._userinfo = response as UserInfo;

        HtmlStorageHelper.saveSessionInfo(this._configuration.userInfoToSerializable
            ? this._configuration.userInfoToSerializable(this._userinfo)
            : this._userinfo);

        return response;
    }

    public async logout(redirect: string): Promise<void> {
        this.clearLoginState();
        location.href = `${this.buildOauthProxyUrl("logout")}?rd=${redirect}`;
    }


    clearLoginState(): void {
        this._lastUserInfo = this._userinfo;
        this._userinfo = null;
        HtmlStorageHelper.clearSessionInfo();
    }


    public async onUserInfoChanged(callback: (currentUserInfo: UserInfo | null, userInfo: UserInfo | null) => void) {
        HtmlStorageHelper.onSessionInfoChange((newUsr) => callback(this._lastUserInfo, newUsr));
    }


    public get userInfo() {
        return this._userinfo;
    }

    userInfoChanged(_oldUserInfo: UserInfo | null, newUserInfo: UserInfo | null): void {
        this._lastUserInfo = this._userinfo;
        this._userinfo = this._configuration.userInfoSerializedToUserInfo
            ? this._configuration.userInfoSerializedToUserInfo(newUserInfo)
            : newUserInfo;;
    }

    private buildOauthProxyUrl(operationType: OAuthAgentOperationType): string {
        return `${this._configuration.oauthProxyBaseUrl}${oauthOperationPaths[operationType]}`;
    }

    private async _callOAuthAgent(method: Method, operationType: OAuthAgentOperationType, body?: any): Promise<any> {
        const url = this.buildOauthProxyUrl(operationType);

        try {

            const options: AxiosRequestConfig = {
                url: url,
                method: method,
                headers: {
                    Accept: "application/json",
                },
                withCredentials: true,
            }

            if (body != null) {
                options.data = body;
                options.headers!["Content-Type"] = "application/json";
            }

            //TODO: Headers para logs?
            const response = await axios.request(options);
            if (response.data != null) {

                //TODO checkJson??
                return response.data;
            }

            return null;


        }
        catch (e: any) {
            throw e;
        }
    }
}



let authService: AuthenticationServiceImpl;

export function configureAuthentication(configuration: AuthenticationServiceConfiguration) {
    authService = new AuthenticationServiceImpl(configuration);
}

export function useAuthentication(): AuthenticationService {
    return authService;
}